import { has, keys } from 'lodash/fp';
import { ZodSchema } from 'zod';

import { getCollectionIds } from '@mews/fp';
import {
    BusinessError,
    createAction,
    logSentryWarning,
    parsePaymentCardExpirationDate,
    validateWithZod,
} from '@mews/framework';

import {
    ConfirmationScreenMandatoryQueryData,
    prepareCheckout,
    preserveConfirmationParams,
    showCheckout,
    showConfirmation,
    showSummary,
} from '../../../../actions/navigation';
import { createReservationGroup } from '../../../../api/actions/reservationGroups';
import { fetchFailedExchangeRates } from '../../../../modules/entities/exchangeRates/actions';
import { shouldAuthorizePaymentCard } from '../../../../modules/entities/paymentCards/actions';
import { getSelectedHotelId } from '../../../../modules/resourceOrder/selectors';
import { pciProxy } from '../../../../services/pciProxy';

import { DistributorThunkAction } from '../../../../types';
import { CHECKOUT_PREVIOUS_STEP_SET } from '../../../confirmation/data/actionTypes';
import { redirectToPaymentGatewayCardAuthorization, redirectToPaymentGatewayPaymentRequest } from '../../data/actions';
import { getCustomerValues, isCheckoutFetching } from '../../data/selectors';
import { shouldCheckoutPreparationStart, shouldDisplayPaymentCardForm } from '../../data/viewSelectors';
import { CHECKOUT_REQUEST_STARTED, CHECKOUT_REQUEST_SUCCEEDED } from './actionTypes';
import {
    areAllFieldsTouched,
    getCustomerValue,
    getCustomerValuesSchema,
    hasCheckoutFailed,
    hasCustomerErrors,
} from './selectors';
import {
    cardEncryptionSuccess,
    clearCustomerErrors,
    confirmReservation,
    failCardEncryption,
    failCheckoutRequest,
    setCustomerValue,
    startCardEncryption,
    touchAllFields,
    validateCustomerValues,
} from './slice';
import { CheckoutCustomerFormValues } from './types';

export function checkoutTryAgain() {
    return (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();
        dispatch(fetchFailedExchangeRates());
        if (shouldCheckoutPreparationStart(state)) {
            dispatch(prepareCheckout());
        } else if (hasCheckoutFailed(state)) {
            dispatch(finishBooking());
        }
    };
}

export function checkoutDismiss() {
    return (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();

        if (shouldCheckoutPreparationStart(state)) {
            dispatch(showSummary());
        } else if (hasCheckoutFailed(state)) {
            dispatch(showCheckout());
        }
    };
}

export function changeCustomerValue({ key, value }: $TSFixMe): DistributorThunkAction {
    return (dispatch) => {
        dispatch(setCustomerValue({ key, value }));
        dispatch(validateField(key));
    };
}

const normalizeErrors = (
    validateWithZodResult: Record<string, { textKey: Mews.TextKey } & Record<string, unknown>>
) => {
    const errors = Object.entries(validateWithZodResult).map(([key, value]) => ({ key, error: value.textKey }));
    return errors;
};

const validateField =
    (key: keyof CheckoutCustomerFormValues): DistributorThunkAction =>
    (dispatch, getState) => {
        const state = getState();

        const value = getCustomerValue(state, { name: key });
        const schema = getCustomerValuesSchema(state, {});

        // @ts-expect-error
        const fieldSchema = schema.pick({ [key]: true });

        const zodResult = validateWithZod(fieldSchema)({ [key]: value });

        if (Object.keys(zodResult).length === 0) {
            // If there are no errors, we can clear the error for this field
            dispatch(validateCustomerValues([{ key, error: undefined }]));
            return;
        }

        const errors = normalizeErrors(zodResult);
        dispatch(validateCustomerValues(errors));
    };

export const handleBookingOwnerChange = (): DistributorThunkAction => (dispatch, getState) => {
    if (areAllFieldsTouched(getState())) {
        dispatch(validateAllValues());
    } else {
        dispatch(validateFieldsWithValues());
    }
};

const validateValues =
    (values: CheckoutCustomerFormValues, schema: ZodSchema): DistributorThunkAction =>
    (dispatch) => {
        const zodResult = validateWithZod(schema)(values);
        const errors = normalizeErrors(zodResult);

        dispatch(clearCustomerErrors());
        dispatch(validateCustomerValues(errors));
    };

const validateFieldsWithValues = (): DistributorThunkAction => (dispatch, getState) => {
    const state = getState();
    const values = getCustomerValues(state);

    const schema = getCustomerValuesSchema(state, {});
    const fieldsWithValues = keys(values);
    // @ts-expect-error
    const partialSchema = schema.pick(Object.fromEntries(fieldsWithValues.map((key) => [key, true])));

    dispatch(validateValues(values, partialSchema));
};

const validateAllValues = (): DistributorThunkAction => (dispatch, getState) => {
    const state = getState();
    const values = getCustomerValues(state);
    const schema = getCustomerValuesSchema(state, {});

    dispatch(validateValues(values, schema));
};

const checkoutRequestStartedAction = createAction(CHECKOUT_REQUEST_STARTED);
const setCheckoutPreviousStepAction = createAction(CHECKOUT_PREVIOUS_STEP_SET);

const checkoutShowConfirmationActionCreator =
    ({
        reservationGroupId,
        enterpriseId,
        reservationIds,
    }: Partial<ConfirmationScreenMandatoryQueryData> = {}): DistributorThunkAction =>
    (dispatch) => {
        dispatch(preserveConfirmationParams({ reservationGroupId, enterpriseId, reservationIds }));
        dispatch(showConfirmation());
        dispatch(confirmReservation());
    };

const afterReservationActionCreator =
    ({ enterpriseId, paymentCardId, paymentRequestId, reservationGroupId, reservationIds }: $TSFixMe) =>
    async (dispatch: $TSFixMe, _getState: $TSFixMe, { loggerService }: $TSFixMe) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (paymentRequestId) {
            dispatch(redirectToPaymentGatewayPaymentRequest({ paymentRequestId, reservationGroupId, reservationIds }));
            return;
        }

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const shouldAuthorizeCard = paymentCardId
            ? await dispatch(shouldAuthorizePaymentCard({ enterpriseId, paymentCardId }))
            : false;

        loggerService.log('payment-card-authorize-result', 'Result for 3DS redirect to card authorization page', {
            paymentCardId,
            enterpriseId,
            reservationGroupId,
            shouldAuthorizeCard,
        });

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (shouldAuthorizeCard) {
            dispatch(
                redirectToPaymentGatewayCardAuthorization({
                    paymentCardId,
                    reservationGroupId,
                    reservationIds,
                })
            );
            return;
        }

        dispatch(setCheckoutPreviousStepAction);
        dispatch(checkoutShowConfirmationActionCreator({ enterpriseId, reservationGroupId, reservationIds }));
    };

export const finishBooking = (): DistributorThunkAction => async (dispatch, getState) => {
    dispatch(touchAllFields());
    dispatch(validateAllValues());

    const state = getState();

    if (!hasCustomerErrors(state) && !isCheckoutFetching(state)) {
        const enterpriseId = getSelectedHotelId(state);

        dispatch(checkoutRequestStartedAction);

        try {
            const creditCardData = await dispatch(getCreditCardData());
            const {
                id: reservationGroupId,
                paymentRequestId,
                paymentCardId,
                reservations,
            } = await dispatch(createReservationGroup({ enterpriseId, creditCardData }));

            dispatch(createAction(CHECKOUT_REQUEST_SUCCEEDED));

            try {
                await dispatch(
                    afterReservationActionCreator({
                        enterpriseId,
                        reservationGroupId,
                        paymentRequestId,
                        paymentCardId,
                        reservationIds: getCollectionIds(reservations),
                    })
                );
            } catch (error) {
                // Errors at this point are not expected and shouldn't affect the user flow, so try showing confirmation screen to the user.
                dispatch(checkoutShowConfirmationActionCreator());
                dispatch(
                    logSentryWarning({
                        error,
                        extra: {
                            step: 'finishBooking - after reservation confirmation',
                        },
                    })
                );
            }
        } catch (error) {
            if (has(['data', 'ExceedingReservationIndexes'], error)) {
                dispatch(showSummary());
            }
            dispatch(failCheckoutRequest({ error }));
        }
    }
};

const getCreditCardData = () => (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const state = getState();

    // without payment card form, resolve to empty data
    if (!shouldDisplayPaymentCardForm(state, {})) {
        return Promise.resolve({
            paymentGatewayData: null,
            obfuscatedCreditCardNumber: null,
        });
    }

    return dispatch(getPciProxyCreditCardData());
};

const getPciProxyCreditCardData =
    () =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { loggerService }: $TSFixMe) => {
        const t = (message: $TSFixMe, params = {}) => loggerService.info('checkout-trace', message, params);

        t('Checkout started');
        const state = getState();
        const customerValues = getCustomerValues(state);
        t('Customer values', customerValues);

        t('Parsing expiration');
        const expirationValue = getCustomerValue(state, { name: 'expiration' });
        // @ts-expect-error $TSFixMeError: Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        const { year, month } = parsePaymentCardExpirationDate(expirationValue);
        t('Expiration parsed', { year, month });
        const expiration = `${year}-${month}`;
        dispatch(startCardEncryption());
        t('Tokenization started');

        return dispatch(pciProxy.actions.tokenizeCard())
            .then((nonce: $TSFixMe) => {
                t('Tokenization successful');
                dispatch(cardEncryptionSuccess());
                const holderName = getCustomerValue(state, { name: 'holderName' });
                return {
                    paymentGatewayData: nonce,
                    holderName,
                    expiration,
                };
            })
            .catch((error: $TSFixMe) => {
                t('Tokenization failed', { error });
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                const message = (error && error.message) || error;
                const businessError = new BusinessError(message);

                dispatch(failCardEncryption({ error: businessError }));
                throw businessError;
            });
    };
